import * as React from 'react';
import {
  Award,
  Building2,
  ImageUp,
  Medal,
  MessageCircle,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { FacultyProfileForm } from 'features/DigitalMarketing/pages/EditWebsiteContent/FacultyProfiles/FacultyProfileForm';
import { FacultyProfileList } from 'features/DigitalMarketing/pages/EditWebsiteContent/FacultyProfiles/FacultyProfileList';
import { InstitutionDetailsForm } from 'features/DigitalMarketing/pages/EditWebsiteContent/InstitutionDetailsForm';
import {
  InfrastructurePhotoManager,
  InfrastructurePhotos,
} from 'features/DigitalMarketing/pages/EditWebsiteContent/InfrastructurePhotos';
import { LeadershipProfileForm } from 'features/DigitalMarketing/pages/EditWebsiteContent/LeadershipProfiles/LeadershipProfileForm';
import { LeadershipProfileList } from 'features/DigitalMarketing/pages/EditWebsiteContent/LeadershipProfiles/LeadershipProfileList';
import { RecognitionAchievementForm } from 'features/DigitalMarketing/pages/EditWebsiteContent/RecognitionsAchievements/RecognitionAchievementForm';
import { RecognitionsAchievements } from 'features/DigitalMarketing/pages/EditWebsiteContent/RecognitionsAchievements';
import { Testimonials } from 'features/DigitalMarketing/pages/EditWebsiteContent/Testimonials';
import { TestimonialForm } from 'features/DigitalMarketing/pages/EditWebsiteContent/Testimonials/TestimonialForm';
import { buildEditWebsiteContentPath } from 'features/DigitalMarketing/routes/editWebsiteContentPaths';

type EditWebsiteContentSection = {
  title: string;
  description: string;
  icon: LucideIcon;
  subPath: string;
};

type EditWebsiteContentRoute = EditWebsiteContentSection & {
  element: React.ReactElement;
  extendedSubPaths?: Array<{
    subPath: string;
    element: React.ReactElement;
  }>;
};

export const EDIT_WEBSITE_CONTENT_ROUTES: EditWebsiteContentRoute[] = [
  {
    title: 'Institution Details',
    description: "Keep your Institution's information accurate and up to date.",
    icon: Building2,
    subPath: 'institution-details',
    element: <InstitutionDetailsForm />,
  },
  {
    title: 'Recognitions & Achievements',
    description: 'Show your success. Build credibility. Earn Parents trust',
    icon: Medal,
    subPath: 'recognitions-achievements',
    element: <RecognitionsAchievements />,
    extendedSubPaths: [
      {
        subPath: 'recognitions-achievements/create-recognition',
        element: <RecognitionAchievementForm />,
      },
      {
        subPath: 'recognitions-achievements/create-achievement',
        element: <RecognitionAchievementForm />,
      },
      {
        subPath: 'recognitions-achievements/edit-recognition',
        element: <RecognitionAchievementForm />,
      },
      {
        subPath: 'recognitions-achievements/edit-achievement',
        element: <RecognitionAchievementForm />,
      },
    ],
  },
  {
    title: 'Upload Infrastructure Photos',
    description:
      "Turn your school website into a lead engine! Showcase your Institution's infrastructure to upto 1 lakh parents!!",
    icon: ImageUp,
    subPath: 'upload-infrastructure-photos',
    element: <InfrastructurePhotos />,
    extendedSubPaths: [
      {
        subPath: 'upload-infrastructure-photos/:categorySlug',
        element: <InfrastructurePhotoManager />,
      },
    ],
  },
  {
    title: 'Leadership Profiles',
    description:
      "Showcase leadership profiles on Institution's website - Founder, Co-Founder, Principal",
    icon: Award,
    subPath: 'leadership-profile-list',
    element: <LeadershipProfileList />,
    extendedSubPaths: [
      {
        subPath: 'leadership-profile-list/create',
        element: <LeadershipProfileForm />,
      },
      {
        subPath: 'leadership-profile-list/edit',
        element: <LeadershipProfileForm />,
      },
    ],
  },
  {
    title: 'Faculty Profiles',
    description: 'Showcase the people behind your Institution success',
    icon: UserCog,
    subPath: 'faculty-profiles',
    element: <FacultyProfileList />,
    extendedSubPaths: [
      { subPath: 'faculty-profiles/create', element: <FacultyProfileForm /> },
      { subPath: 'faculty-profiles/edit', element: <FacultyProfileForm /> },
    ],
  },
  {
    title: 'Testimonials',
    description:
      'AI collects professional testimonials with Name, Relation, Feedback, and Rating.',
    icon: MessageCircle,
    subPath: 'testimonials',
    element: <Testimonials />,
    extendedSubPaths: [
      { subPath: 'testimonials/create', element: <TestimonialForm /> },
      { subPath: 'testimonials/edit', element: <TestimonialForm /> },
    ],
  },
];

export const WEBSITE_CONTENT_SECTIONS = EDIT_WEBSITE_CONTENT_ROUTES.map(
  ({ title, description, icon, subPath }) => ({
    title,
    description,
    icon,
    path: buildEditWebsiteContentPath(subPath),
  })
);

export const EDIT_WEBSITE_CONTENT_SUB_ROUTES =
  EDIT_WEBSITE_CONTENT_ROUTES.flatMap(route => [
    {
      path: `edit-website-content/${route.subPath}`,
      element: route.element,
    },
    ...(route.extendedSubPaths || []).map(extendedRoute => ({
      path: `edit-website-content/${extendedRoute.subPath}`,
      element: extendedRoute.element,
    })),
  ]);
